import {
  IconButton,
  Tooltip,
  Zoom,
  Chip,
  Typography,
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  Paper,
} from '@mui/material';
import PropTypes from 'prop-types';
import PhoneIcon from '../TrackerFilter/Icons/PhoneIcon';
import SmartphoneIcon from '../TrackerFilter/Icons/SmartPhoneIcon';
import EmailIcon from '../TrackerFilter/Icons/EmailIcon';
import CommentIcon from '../TrackerFilter/Icons/CommentIcon';
import PositiveIcon from '../TrackerFilter/Icons/PositiveIcon';
import NeutralIcon from '../TrackerFilter/Icons/NeutralIcon';
import NegativeIcon from '../TrackerFilter/Icons/NegativeIcon';
import AttachmentIcon from '../TrackerFilter/Icons/AttachmentIcon';
import LinkedServiceIcon from '../TrackerFilter/Icons/LinkedServiceIcon';

import { UploadCell } from './CellTypes';
import ErrorBoundary from '../ErrorBoundary';
import { useFiltersDispatch } from '../TrackerFilter/Context/FiltersContext';
import { Link } from 'react-router-dom';
import {
  ContactMethodName,
  complexityOptions,
  TrackerDataType,
  mediaToneOptions,
} from '../../shared/constants/constants';
import {
  ContentLogKeys,
  IssueAndServiceSharedValues,
  IssueKeys,
  ServiceKeys,
  inProgressKeys,
  EngagementKeys,
  IssueAndEngagementSharedValues,
} from '../../shared/constants/IssueAndServiceKeys';
import RichTextEditor from '../RichTextEditor/RichTextEditor';
import { RouteInformation } from '../../shared/constants/RouteInformation';
import { useDialog } from '../../shared/contexts/DialogContext';
import CloseIcon from '@mui/icons-material/Close';
import truncate from 'html-truncate';
import AddOnProtectedWrapper from '../../shared/protectedWrappers/AddOnProtectedWrapper';
import { AddOnTypes } from '../../shared/constants/TenantFields';
import dayjs from '../../utils/dayjs';
import { styled } from '@mui/material/styles';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import MadeByAutoTrackerIcon from '../TrackerFilter/Icons/MadeByAutoTrackerIcon';

// Styled tooltip component for "Click to Copy" tooltips
const WhiteBackgroundTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .MuiTooltip-tooltip`]: {
    backgroundColor: 'white',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    padding: '8px 10px',
    gap: '8px',
    boxShadow: '0px 0px 4px rgba(126, 126, 126, 0.25)',
    borderRadius: '5px',
  },
  [`& .MuiTooltip-arrow`]: {
    color: 'white',
  },
}));

const TooltipTitle = styled(Typography)({
  fontWeight: 400,
  fontSize: '12px',
  color: '#A3A3A3',
});

const TooltipContent = styled(Typography)({
  fontSize: '14px',
  color: 'black',
});

const ClickToCopyTooltip = ({ content, icon }) => {
  const displayContent = content || 'N/A';

  return (
    <WhiteBackgroundTooltip
      title={
        <Box>
          <TooltipTitle>Click to copy</TooltipTitle>
          <TooltipContent>{displayContent}</TooltipContent>
        </Box>
      }
      arrow
    >
      <IconButton
        style={{ padding: 4 }}
        onClick={() => navigator.clipboard.writeText(displayContent)}
        size="large"
        // Visual indication when no content available
        sx={!content ? { opacity: 0.5 } : {}}
      >
        {icon}
      </IconButton>
    </WhiteBackgroundTooltip>
  );
};

const renderContactInfo = (obj, styles) => {
  const iconSize = { fontSize: '20px' };
  return (
    <Box>
      <Typography variant="body2" sx={styles}>
        {obj.name}
      </Typography>
      <Box display="inline-flex">
        <ClickToCopyTooltip content={obj.phone} icon={<PhoneIcon style={iconSize} />} />
        <ClickToCopyTooltip content={obj.cell} icon={<SmartphoneIcon style={iconSize} />} />
        <ClickToCopyTooltip content={obj.email} icon={<EmailIcon style={iconSize} />} />
        <ClickToCopyTooltip content={obj.notes} icon={<CommentIcon style={iconSize} />} />
      </Box>
    </Box>
  );
};

const CellViewerTextDialog = ({ payload, open, onClose }) => {
  let titleColor = 'primary';
  if (payload.type === 'mediaInteraction') {
    titleColor = 'mediaInteraction.main';
  }

  return (
    <Dialog fullWidth maxWidth="md" open={open} onClose={() => onClose()}>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Typography>{payload.titleName}</Typography>
            <Typography color={titleColor}>{payload.title}</Typography>
          </Box>
          <Button
            sx={{
              backgroundColor: '#AAAAAA',
              '&:hover': {
                backgroundColor: '#AAAAB0',
              },
            }}
            variant="contained"
            onClick={() => onClose()}
            endIcon={<CloseIcon />}
          >
            Close
          </Button>
        </Box>
      </DialogTitle>
      <DialogContent>
        {payload.cellName}
        <Paper variant="outlined" sx={{ padding: '1rem', marginTop: '1rem' }}>
          <RichTextEditor
            defaultValue={''}
            value={payload.content}
            readOnly={true}
            quillStyle={{ resize: 'vertical' }}
          />
        </Paper>
      </DialogContent>
    </Dialog>
  );
};

// Helper function to create tooltip with icon
const createTooltipWithIcon = (title, icon, mr = 0.5) => (
  <Tooltip title={<Typography variant={'body2'}>{title}</Typography>} TransitionComponent={Zoom} enterDelay={200}>
    <Box component="span" sx={{ mr }}>
      {icon}
    </Box>
  </Tooltip>
);

const hasCellValue = (value) => {
  if (Array.isArray(value)) {
    return value.length > 0;
  }
  if (typeof value === 'string') {
    return value.trim().length > 0;
  }
  return Boolean(value);
};

const getFirstExistingCellValue = (cells, keys) => {
  if (!keys) {
    return null;
  }
  const keyList = Array.isArray(keys) ? keys : [keys];
  for (const key of keyList) {
    if (hasCellValue(cells?.[key])) {
      return cells[key];
    }
  }
  return null;
};

// Helper function to create a topic box with proper styling
const createTopicBox = (
  content,
  color,
  fileKey,
  linkedServiceKey,
  cells,
  { fileTooltipText, linkedServiceTooltipText, autotrackerTooltipText } = {}
) => {
  const fileValue = getFirstExistingCellValue(cells, fileKey);
  const linkedServiceValue = getFirstExistingCellValue(cells, linkedServiceKey);
  const hasAutotracker =
    cells[IssueAndServiceSharedValues.autotracker] &&
    Object.keys(cells[IssueAndServiceSharedValues.autotracker]).length > 0;

  return (
    <Box sx={{ paddingLeft: '6px' }}>
      <Typography variant="body2" sx={{ textAlign: 'left', color }}>
        {content}
      </Typography>
      {(fileKey || linkedServiceKey) && (
        <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
          {fileValue ? createTooltipWithIcon(fileTooltipText, <AttachmentIcon />) : null}
          {linkedServiceValue ? createTooltipWithIcon(linkedServiceTooltipText, <LinkedServiceIcon />, 0) : null}
          {hasAutotracker ? createTooltipWithIcon(autotrackerTooltipText, <MadeByAutoTrackerIcon />, 0) : null}
        </Box>
      )}
    </Box>
  );
};

export default function CellViewer({
  index,
  cells,
  dropdownTable,
  callback,
  openForm = () => {},
  openSecondaryForm = () => {},
  openTertiaryForm = () => {},
  id,
  classes,
  type,
  handleDrag,
  handleDrop,
  isDragActive,
  isSharedResource = false,
  onEditItem,
}) {
  let cellData;
  const findExpertName = (id) => {
    return dropdownTable['expert']?.find?.((expert) => expert['_id'] === id)?.name ?? id;
  };

  const dispatch = useFiltersDispatch();
  const { openDialog } = useDialog();

  const leftAlignedStyle = {
    textAlign: 'left',
    paddingLeft: '6px',
  };

  const normalizeEngagementCommunityMembers = (communityMembers, dropdownTable) => {
    if (!communityMembers) return [];
    const ids = Array.isArray(communityMembers) ? communityMembers : [communityMembers];
    if (!dropdownTable?.communityMember) return ids;
    return ids.map((id) => dropdownTable.communityMember.find((cm) => cm._id === (id?._id ?? id))).filter(Boolean);
  };

  const normalizeOrganizations = (organizations, dropdownTable) => {
    if (!organizations) return [];
    const items = Array.isArray(organizations) ? organizations : [organizations];
    return items
      .map((item) => {
        if (item?.name) {
          return item;
        }
        if (dropdownTable?.organization) {
          const id = item?._id ?? item;
          return dropdownTable.organization.find((org) => org._id === id);
        }
        return item;
      })
      .filter(Boolean);
  };

  const contactMethodLabelMap = {
    [ContactMethodName.NO_INTERMEDIARY]: 'in person',
    [ContactMethodName.PHONE]: 'phone call',
  };

  const getContactMethodLabel = (value) => contactMethodLabelMap[value] || value || '';

  const getTypeNamesForLinkedContentLog = (contentLog) => {
    const types = contentLog?.types ?? contentLog?.type ?? contentLog?.typeNames ?? [];
    const typeList = Array.isArray(types) ? types : [types];
    return typeList
      .map((type) => {
        if (!type) {
          return '';
        }
        if (typeof type === 'string') {
          return type;
        }
        if (typeof type === 'object' && type.name) {
          return type.name;
        }
        return '';
      })
      .filter(Boolean);
  };

  switch (index) {
    case IssueKeys.contactMethod:
    case EngagementKeys.contactMethod:
      cellData = (
        <Typography variant="body2" sx={leftAlignedStyle}>
          {getContactMethodLabel(cells[index])}
        </Typography>
      );
      break;
    case IssueAndServiceSharedValues.leadExpert:
      cellData = (
        <Typography variant="body2" sx={leftAlignedStyle}>
          {cells[index]?.map((item) => (item.name ? item.name : findExpertName(item)))?.join(', ') ?? cells[index]}
        </Typography>
      );
      break;
    case IssueAndServiceSharedValues.teamMembers:
      cellData = (
        <Typography variant="body2" sx={leftAlignedStyle}>
          {cells[index]
            ?.map?.((c) => {
              if (c?.name) {
                return c?.name;
              }
              const user = dropdownTable['lead']?.find((lead) => lead['_id'] === c);
              return user ? user.name : null;
            })
            .join(', \n') ?? dropdownTable['lead']?.find?.((lead) => lead['_id'] === cells[index])?.name}
        </Typography>
      );
      break;
    case IssueAndServiceSharedValues.entryDate:
      cellData = (
        <Typography variant="body2" sx={leftAlignedStyle}>
          {dayjs(cells[index]).format('M/D/YYYY')}
        </Typography>
      );
      break;
    case ServiceKeys.dueDate:
    case IssueKeys.dueDate:
      if (!cells[index]) {
        cellData = null;
        break;
      }
      cellData = (
        <Typography variant="body2" sx={leftAlignedStyle}>
          {dayjs(cells[index]).format('M/D/YYYY')}
        </Typography>
      );
      break;
    case IssueKeys.journalist: {
      let obj;
      if (isSharedResource) {
        obj = cells[index];
      } else {
        obj = dropdownTable['journalist']?.find((journalist) => journalist['_id'] === cells[index]);
      }
      cellData = obj ? renderContactInfo(obj, leftAlignedStyle) : null;
      break;
    }
    case EngagementKeys.communityMember: {
      const communityMembers = normalizeEngagementCommunityMembers(cells[index], dropdownTable);
      if (communityMembers.length === 0) {
        cellData = null;
        break;
      }
      cellData = (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          {communityMembers.map((communityMember) => (
            <Box key={communityMember._id || communityMember.id}>
              {renderContactInfo(communityMember, leftAlignedStyle)}
            </Box>
          ))}
        </Box>
      );
      break;
    }
    case ContentLogKeys.audience: {
      const audienceIds = Array.isArray(cells[index]) ? cells[index] : [cells[index]];
      const audienceRows = (dropdownTable?.audience || []).filter((audience) =>
        audienceIds.some((candidate) => (candidate?._id ?? candidate) === audience._id)
      );

      if (audienceRows.length === 0) {
        cellData = null;
        break;
      }

      cellData = (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'flex-start', paddingLeft: '6px' }}>
          {audienceRows.map((audience) => (
            <Box
              key={audience._id}
              sx={{
                display: 'flex',
                alignItems: 'center',
                borderRadius: '4px',
                overflow: 'hidden',
                width: 'fit-content',
              }}
            >
              <Box
                sx={{
                  width: '7px',
                  height: '30px',
                  bgcolor: audience?.backgroundColor || '#9E9E9E',
                  mr: 0.8,
                  borderRadius: '3px',
                  flexShrink: 0,
                }}
              />
              <Typography sx={{ textAlign: 'left', fontSize: '14px', fontWeight: 400 }}>{audience.name}</Typography>
            </Box>
          ))}
        </Box>
      );
      break;
    }
    case IssueKeys.outlet:
      if (isSharedResource) {
        const obj = cells[index];
        cellData = obj ? (
          <Typography variant="body2" sx={leftAlignedStyle}>
            {obj.name}
          </Typography>
        ) : null;
        break;
      }
    // eslint-disable-next-line no-fallthrough
    case inProgressKeys.outlet:
      cellData = (
        <Typography variant="body2" sx={leftAlignedStyle}>
          {dropdownTable['outlet']?.find((org) => org['_id'] === cells[index])?.name ?? null}
        </Typography>
      );
      break;
    case EngagementKeys.organization: {
      const organizations = normalizeOrganizations(cells[index], dropdownTable);
      if (!organizations.length) {
        cellData = null;
        break;
      }
      const display = organizations
        .map((org) => org.name || '')
        .filter(Boolean)
        .join(', ');

      cellData = (
        <Typography variant="body2" sx={leftAlignedStyle}>
          {display}
        </Typography>
      );
      break;
    }
    case IssueAndServiceSharedValues.type: {
      const typeColorMap = {
        'Red': '#FFBDAD',
        'Green': '#B3F5FF',
        'None': undefined,
      };

      cellData = (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'flex-start', paddingLeft: '6px' }}>
          {cells[index].map((c, i) => {
            const chipColor = typeColorMap[c.backgroundColor];

            return (
              <Chip
                key={`${c.name}-${i}`}
                label={<Box sx={{ marginTop: '5px', marginBottom: '5px' }}>{c.name}</Box>}
                variant="filled"
                sx={{
                  backgroundColor: chipColor,
                  borderRadius: '8px',
                  height: '32px',
                  fontSize: '10px',
                }}
              />
            );
          })}
        </Box>
      );
      break;
    }
    case IssueKeys.linkedService: {
      let payload;
      if (type === TrackerDataType.MEDIA_INTERACTION) {
        payload = {
          linkedInteraction: {
            _id: id,
          },
        };
      } else if (type === TrackerDataType.ENGAGEMENT) {
        payload = {
          linkedEngagement: {
            _id: id,
          },
        };
      } else if (type === TrackerDataType.CONTENT_LOG) {
        payload = {
          linkedContentLog: {
            _id: id,
          },
        };
      }
      cellData = (
        <Box className={classes.stackBox}>
          {id && (
            <Link
              to={RouteInformation.serviceLog.path}
              onClick={() => {
                dispatch({
                  type: 'setFilters',
                  payload,
                });
              }}
            >
              View Linked Service
            </Link>
          )}
          <Box className={classes.stackBox}>
            {cells[index].map((c) => (
              <Typography sx={{ fontSize: '14px' }}>{c.name}</Typography>
            ))}
          </Box>
          {cells[index].length === 0 && (
            <Tooltip title={'Create a new Linked Service'}>
              <IconButton
                variant="contained"
                className={classes.addLinkedButton}
                onClick={() => openForm()}
                data-cy="add-new-linked-service-button-CellViewer"
              >
                <AddCircleIcon />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      );
      break;
    }
    case IssueAndServiceSharedValues.campaign: {
      let sanitizedData = Array.isArray(cells[index]) ? cells[index] : [cells[index]];

      if (sanitizedData.length === 0) {
        cellData = <Typography variant="body2" sx={leftAlignedStyle}></Typography>;
      } else {
        cellData = (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'flex-start', paddingLeft: '6px' }}>
            {sanitizedData.map((campaign, i) => (
              <Box
                key={`${campaign?.name || campaign}-${i}`}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  borderRadius: '4px',
                  overflow: 'hidden',
                  width: 'fit-content',
                }}
              >
                <Box
                  sx={{
                    width: '7px',
                    height: '30px',
                    bgcolor: campaign?.backgroundColor || '#9E9E9E',
                    mr: 0.8,
                    borderRadius: '3px 3px 3px 3px',
                    flexShrink: 0,
                  }}
                />
                <Typography sx={{ textAlign: 'left', fontSize: '14px', fontWeight: 400 }}>
                  {campaign?.name || campaign}
                </Typography>
              </Box>
            ))}
          </Box>
        );
      }

      break;
    }
    case IssueAndServiceSharedValues.department:
    case IssueAndServiceSharedValues.unit: {
      let display = '';

      if (cells[index] instanceof Array && cells[index].length > 0) {
        display = cells[index]?.map?.((c) => `${c}`)?.join(', \n');
      } else if (typeof cells[index] === 'string') {
        display = cells[index];
      }

      cellData = (
        <Typography variant="body2" sx={leftAlignedStyle}>
          {display}
        </Typography>
      );
      break;
    }
    case ServiceKeys.linkedInteraction: {
      cellData =
        cells[index]?.interactions?.length > 0 ? (
          <Box className={classes.stackBox}>
            <Link
              to={RouteInformation.mediaInteraction.path}
              onClick={() => {
                dispatch({
                  type: 'setFilters',
                  payload: {
                    linkedService: [
                      {
                        _id: cells[index]._id,
                        name: cells[index].name,
                        firstLetter: cells[index].name.charAt(0),
                      },
                    ],
                  },
                });
              }}
            >
              View Linked Interactions
            </Link>
            {cells[index].interactions.map((c) => (
              <Typography key={c.outlet} sx={{ fontSize: '14px' }}>
                {c.outlet}
              </Typography>
            ))}
            <Tooltip title={'Add an interaction'}>
              <IconButton
                variant="contained"
                className={classes.addLinkedButton}
                onClick={() => openForm()}
                data-cy="add-new-linked-interaction-button-CellViewer"
              >
                <AddCircleIcon />
              </IconButton>
            </Tooltip>
          </Box>
        ) : (
          <Box className={classes.stackBox}>
            <Typography sx={{ fontSize: '14px' }}>No Media Interactions Yet</Typography>
            <Tooltip title={'Add an interaction'}>
              <IconButton
                variant="contained"
                className={classes.addLinkedButton}
                onClick={() => openForm()}
                data-cy="add-new-linked-interaction-button-CellViewer"
              >
                <AddCircleIcon />
              </IconButton>
            </Tooltip>
          </Box>
        );

      // TODO check placement and icons
      const resolveCommunityMemberNames = (engagement, dropdownTable) => {
        const { communityMembers, communityMember } = engagement;
        if (Array.isArray(communityMembers)) {
          return communityMembers
            .map((member) => {
              if (typeof member === 'string') {
                return dropdownTable.communityMember?.find((cm) => cm._id === member)?.name ?? member;
              }
              return member?.name ?? '';
            })
            .filter(Boolean)
            .join(', ');
        }
        if (communityMember) {
          return communityMember;
        }
        return '';
      };

      const getLinkedEngagementsSummary = (linkedEngagementCell, dropdownTable) => {
        const engagements = linkedEngagementCell?.engagements;
        if (!Array.isArray(engagements)) {
          return [];
        }

        return engagements
          .map((engagement, index) => {
            const names = resolveCommunityMemberNames(engagement, dropdownTable);
            if (!names) {
              return null;
            }

            return {
              key: engagement._id ?? engagement.id ?? `${names}-${index}`,
              text: names,
            };
          })
          .filter(Boolean);
      };

      const linkedEngagementsSummary = getLinkedEngagementsSummary(cells[ServiceKeys.linkedEngagement], dropdownTable);

      const engagementCellData =
        cells[ServiceKeys.linkedEngagement]?.engagements?.length > 0 ? (
          <Box className={classes.stackBox}>
            <Link
              to={RouteInformation.engagementLog.path}
              onClick={() => {
                dispatch({
                  type: 'setFilters',
                  payload: {
                    linkedService: [
                      {
                        _id: cells[index]._id,
                        name: cells[index].name,
                        firstLetter: cells[index].name.charAt(0),
                      },
                    ],
                  },
                });
              }}
            >
              View Linked Engagements
            </Link>
            {linkedEngagementsSummary.map((s) => (
              <Typography key={s.key} sx={{ fontSize: '14px' }}>
                {s.text}
              </Typography>
            ))}
            <Tooltip title={'Add an engagement'}>
              <IconButton
                variant="contained"
                className={classes.addLinkedButton}
                onClick={() => openSecondaryForm()}
                data-cy="add-new-linked-engagement-button-CellViewer"
              >
                <AddCircleIcon />
              </IconButton>
            </Tooltip>
          </Box>
        ) : (
          <Box className={classes.stackBox}>
            <Typography sx={{ fontSize: '14px' }}>No Engagements Yet</Typography>
            <Tooltip title={'Add an engagement'}>
              <IconButton
                variant="contained"
                className={classes.addLinkedButton}
                onClick={() => openSecondaryForm()}
                data-cy="add-new-linked-engagement-button-CellViewer"
              >
                <AddCircleIcon />
              </IconButton>
            </Tooltip>
          </Box>
        );
      const serviceName = cells[index]?.name || cells[ServiceKeys.service] || '';
      const linkedServicePayload = cells[index]?._id
        ? [
            {
              _id: cells[index]._id,
              name: serviceName,
              firstLetter: serviceName ? serviceName.charAt(0) : '',
            },
          ]
        : [];

      const linkedContentLogs = Array.isArray(cells[ServiceKeys.linkedContentLog]?.contentLogs)
        ? cells[ServiceKeys.linkedContentLog].contentLogs
        : [];
      const linkedContentTypeRows = linkedContentLogs
        .map((contentLog) => {
          const typeNames = getTypeNamesForLinkedContentLog(contentLog);
          return typeNames.length > 0 ? typeNames.join(', ') : '';
        })
        .filter(Boolean);

      const contentCellData =
        linkedContentTypeRows.length > 0 ? (
          <Box className={classes.stackBox}>
            <Link
              to={RouteInformation.contentLog.path}
              onClick={() => {
                dispatch({
                  type: 'setFilters',
                  payload: {
                    linkedService: linkedServicePayload,
                  },
                });
              }}
            >
              View Contents
            </Link>
            {linkedContentTypeRows.map((typeRow, rowIndex) => (
              <Typography key={`${typeRow}-${rowIndex}`} sx={{ fontSize: '14px' }}>
                {typeRow}
              </Typography>
            ))}
            <Tooltip title={'Add a content log'}>
              <IconButton
                variant="contained"
                className={classes.addLinkedButton}
                onClick={() => openTertiaryForm()}
                data-cy="add-new-linked-content-button-CellViewer"
              >
                <AddCircleIcon />
              </IconButton>
            </Tooltip>
          </Box>
        ) : (
          <Box className={classes.stackBox}>
            <Typography sx={{ fontSize: '14px' }}>No Content Items Yet</Typography>
            <Tooltip title={'Add a content log'}>
              <IconButton
                variant="contained"
                className={classes.addLinkedButton}
                onClick={() => openTertiaryForm()}
                data-cy="add-new-linked-content-button-CellViewer"
              >
                <AddCircleIcon />
              </IconButton>
            </Tooltip>
          </Box>
        );
      cellData = (
        <>
          {cellData}
          <AddOnProtectedWrapper addOnType={AddOnTypes.engagementLog}>{engagementCellData}</AddOnProtectedWrapper>
          <AddOnProtectedWrapper addOnType={AddOnTypes.contentLog}>{contentCellData}</AddOnProtectedWrapper>
        </>
      );

      break;
    }
    case IssueKeys.topic:
      if (type === TrackerDataType.CONTENT_LOG) {
        cellData = createTopicBox(
          cells[index],
          'black',
          [ContentLogKeys.file, 'files'],
          [
            ContentLogKeys.linkedService,
            IssueAndEngagementSharedValues.linkedService,
            'linkedServices',
            'linkedService',
          ],
          cells,
          {
            fileTooltipText: 'Content Log has attached drafts, images, or links',
            linkedServiceTooltipText: 'Content Log has been linked to a Service',
          }
        );
      } else if (type === TrackerDataType.ENGAGEMENT) {
        cellData = createTopicBox(
          cells[index],
          'black',
          EngagementKeys.file,
          IssueAndEngagementSharedValues.linkedService,
          cells,
          {
            fileTooltipText: 'Engagement has attached links or documents',
            linkedServiceTooltipText: 'Engagement has been linked to a Service',
          }
        );
      } else {
        cellData = createTopicBox(
          cells[index],
          'black',
          IssueKeys.file,
          IssueAndEngagementSharedValues.linkedService,
          cells,
          {
            fileTooltipText: 'Interaction has attached links',
            linkedServiceTooltipText: 'Interaction has been linked to a Service',
            autotrackerTooltipText: 'Interaction has been created through AutoTracker',
          }
        );
      }
      break;
    case ServiceKeys.service:
      cellData = createTopicBox(cells[index], 'black', ServiceKeys.file, null, cells, {
        fileTooltipText: 'Service has attached links or documents',
        autotrackerTooltipText: 'Service has been created through AutoTracker',
      });
      break;
    case IssueAndServiceSharedValues.backgroundResponse:
    case EngagementKeys.background:
    case ContentLogKeys.background:
    case ContentLogKeys.content:
    case ContentLogKeys.otherNotes:
    case EngagementKeys.notes:
    case IssueAndServiceSharedValues.keyMessaging: {
      const addonKey =
        index === IssueAndServiceSharedValues.backgroundResponse || index === EngagementKeys.background
          ? 'response'
          : 'keyMessaging';

      let data = cells[index] ? cells[index] : '';
      if (
        (type === TrackerDataType.MEDIA_INTERACTION || type === TrackerDataType.ENGAGEMENT) &&
        cells[IssueAndEngagementSharedValues.fieldAddons]?.[addonKey]
      ) {
        data = `${data !== '' ? data + '\n' : ''}---- Copied from Linked Service ---- \n${
          cells[IssueAndEngagementSharedValues.fieldAddons][addonKey]
        }`;
      }

      let displayComponent = <RichTextEditor defaultValue={''} value={data} readOnly={true} />;
      const TRUNCATE_LIMIT = 350;

      if (data && data.length > TRUNCATE_LIMIT) {
        const displayData = truncate(data, TRUNCATE_LIMIT);
        const dialogType = cells['Service'] ? 'service' : 'mediaInteraction';
        const dialogTitleName = dialogType === 'service' ? 'Service' : 'Topic';
        const dialogTitle = dialogType === 'service' ? cells['Service'] : cells['Topic'];

        displayComponent = (
          <>
            <RichTextEditor
              defaultValue={''}
              value={displayData}
              readOnly={true}
              quillStyle={{
                minHeight: 130,
                maxHeight: 130,
                width: '100%',
                overflow: 'hidden',
              }}
            />
            <Button
              variant="outlined"
              sx={{ marginTop: '1rem', width: '100%' }}
              onClick={() =>
                openDialog(CellViewerTextDialog, {
                  type: dialogType,
                  titleName: dialogTitleName,
                  title: dialogTitle,
                  cellName: index.toUpperCase(),
                  content: data,
                })
              }
            >
              Read More
            </Button>
          </>
        );
      }

      cellData = (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
          {displayComponent}
        </div>
      );
      break;
    }
    case IssueKeys.file:
      cellData = (
        <UploadCell
          id={id}
          cellName={index}
          files={cells[index]}
          callback={callback}
          type={type}
          onEditItem={onEditItem}
        />
      );
      break;
    case ServiceKeys.file:
    case EngagementKeys.file:
      cellData = (
        <UploadCell
          id={id}
          cellName={index}
          files={cells[index]}
          type={type}
          callback={callback}
          handleDrag={handleDrag}
          handleDrop={handleDrop}
          isDragActive={isDragActive}
          onEditItem={onEditItem}
        />
      );
      break;
    case ContentLogKeys.file:
      cellData = (
        <UploadCell
          id={id}
          cellName={index}
          files={cells[index]}
          callback={callback}
          type={type}
          handleDrag={handleDrag}
          handleDrop={handleDrop}
          isDragActive={isDragActive}
          onEditItem={onEditItem}
        />
      );
      break;
    case ContentLogKeys.liveLink:
      cellData = cells[index] ? (
        <UploadCell
          id={id}
          cellName={index}
          files={[cells[index]]}
          callback={(updatedFiles) => callback(updatedFiles?.[0] ?? null, index)}
          type={type}
          onEditItem={onEditItem}
        />
      ) : null;
      break;
    case ServiceKeys.complexity:
      if (cells[index]) {
        const complexityOption = complexityOptions.find((option) => option._id === cells[index]);
        cellData = complexityOption ? (
          <Chip
            label={complexityOption._id}
            variant="filled"
            sx={{
              '& .MuiChip-label': {
                padding: 0,
              },
            }}
          />
        ) : (
          cells[index]
        );
      } else {
        cellData = '';
      }
      break;
    case IssueKeys.tone: {
      const toneIcons = {
        [mediaToneOptions[0]._id]: <PositiveIcon />,
        [mediaToneOptions[1]._id]: <NeutralIcon />,
        [mediaToneOptions[2]._id]: <NegativeIcon />,
      };

      cellData = (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
          }}
        >
          {toneIcons[cells[index]] || ''}
        </Box>
      );
      break;
    }
    // temporarily hidden
    // case ServiceKeys.diversity:
    //   cellData = (
    //     <Typography variant="body2" style={{ minWidth: '150px' }}>
    //       {cells[index]}
    //     </Typography>
    //   );
    //   break;
    case IssueAndServiceSharedValues.status:
      cellData = (
        <Chip label={cells[index] === 'Participation Ribbon' ? 'Participation' : cells[index]} variant="filled" />
      );
      break;
    default:
      cellData = cells[index] ?? '';
      break;
  }
  return <ErrorBoundary>{cellData}</ErrorBoundary>;
}

CellViewer.propTypes = {
  index: PropTypes.string.isRequired,
  cells: PropTypes.object.isRequired,
  dropdownTable: PropTypes.shape({
    expert: PropTypes.array,
    communityMember: PropTypes.array,
    organization: PropTypes.array,
    audience: PropTypes.array,
    lead: PropTypes.array,
    journalist: PropTypes.array,
    outlet: PropTypes.array,
  }).isRequired,
  callback: PropTypes.func,
  openForm: PropTypes.func,
  openSecondaryForm: PropTypes.func,
  openTertiaryForm: PropTypes.func,
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  classes: PropTypes.shape({
    stackBox: PropTypes.string,
    addLinkedButton: PropTypes.string,
  }).isRequired,
  type: PropTypes.string,
  handleDrag: PropTypes.func,
  handleDrop: PropTypes.func,
  isDragActive: PropTypes.bool,
  isSharedResource: PropTypes.bool,
  onEditItem: PropTypes.func,
};
